/**
 * Landing on the Candidates list (refresh, link, bookmark), its request waited
 * for the shell (user, permissions, saved filters) and then the list's lazy
 * chunk. Here both the chunk and the last first-page request start during
 * boot, alongside the bootstrap calls.
 *
 * The chunk import must match the `/candidates` route in
 * `components/getRoutes` (the ATS list) so both resolve to one chunk.
 */
import { store } from '@/store';
import { contactsOldApi } from '@/store/query/contacts-list';
import {
  markBootPrefetch,
  readLastContactsRequest,
} from '@/pages/contacts/list-revamp/lastContactsRequest';

let requested = false;

export const isContactsListPath = (pathname: string): boolean =>
  pathname === '/candidates' || pathname === '/candidates/';

// Entries that make the list build a different first request: a global-search
// hand-off, the consent-status shortcut, or a deep link to a later page.
const REPLAY_BLOCKERS = ['search', 'consent_status', 'page'];

export const preloadContactsListRoute = (
  location: Pick<Location, 'pathname' | 'search'> = window.location
): void => {
  if (requested || !isContactsListPath(location.pathname)) return;
  requested = true;

  import('@/pages/contacts/list-revamp').catch(() => {
    // A failed preload must not surface — React.lazy retries on navigation.
    requested = false;
  });

  const query = new URLSearchParams(location.search);
  if (REPLAY_BLOCKERS.some((key) => query.has(key))) return;
  // Without a session the call would only bounce off auth.
  try {
    if (!localStorage.getItem('apiv1Auth')) return;
  } catch {
    return;
  }

  const payload = readLastContactsRequest();
  if (!payload) return;

  markBootPrefetch(payload);
  const request = store.dispatch(
    contactsOldApi.endpoints.getAllContacts.initiate({ payload })
  );
  // Hold the entry only until it settles; RTK keeps it cached for the list.
  request.then(
    () => request.unsubscribe(),
    () => request.unsubscribe()
  );
};
